<template>
  <div class="workstream-header" :class="{ collapsed: !expanded }">
    <div class="header-toggle" @click="expanded = !expanded">
      <div class="header-summary">
        <span class="header-title">{{ workstream?.name || 'Workstream' }}</span>
        <span class="header-stats">
          <span class="stat-item">{{ taskCount }} tasks</span>
          <span v-if="agentCount" class="stat-item">⚡ {{ agentCount }}</span>
          <span v-if="memberCount" class="stat-item">{{ memberCount }} members</span>
        </span>
        <div v-if="overallProgress !== null" class="overall-progress">
          <span class="overall-progress-label">Progress <span class="overall-progress-value">{{ overallProgress }}%</span></span>
          <div class="overall-progress-bar">
            <div class="overall-progress-fill progress-fill" :style="{ width: `${overallProgress}%` }" :class="getProgressClass(overallProgress)"></div>
          </div>
        </div>
      </div>
      <button class="toggle-btn">
        {{ expanded ? '▼' : '▶' }}
      </button>
    </div>

    <div v-if="expanded" class="header-content">
      <div class="overview-section">
        <label>Description:</label>
        <p v-if="!editingOverview" @click="startEditOverview" class="overview-text">
          {{ workstream?.description || 'Click to add description...' }}
        </p>
        <div v-else class="edit-container">
          <textarea 
            v-model="overviewDraft"
            @keydown.enter.meta="saveOverview"
            @keydown.esc="cancelOverview"
            class="overview-input"
            rows="2"
            ref="overviewInput"
          />
          <div class="edit-actions">
            <button @click="cancelOverview" class="btn-cancel">Cancel</button>
            <button @click="saveOverview" class="btn-save">Save</button>
          </div>
        </div>
      </div>

      <div class="goals-section">
        <div class="goals-header">
          <label>Goals:</label>
          <div class="header-actions">
            <button @click="$emit('add-task')" class="btn-add-goal">+ Add Task</button>
            <button @click="addGoal" class="btn-add-goal" :disabled="creatingGoal || !workstream?.id">+ Add Goal</button>
            <button v-if="usingLegacyGoals" @click="importLegacyGoals" class="btn-add-goal" title="Import embedded goals to Goals API">⇪ Import Legacy</button>
          </div>
        </div>
        <div v-if="goals.length > 0" class="goals-list">
          <div v-for="goal in goals" :key="goal.id" class="goal-item">
            <div class="goal-main">
              <div class="goal-header">
                <div class="goal-content">
                  <span class="goal-bullet">•</span>
                  <input 
                    v-if="editingGoalId === goal.id"
                    v-model="goal.title"
                    @blur="saveGoal(goal)"
                    @keydown.enter="saveGoal(goal)"
                    @keydown.esc="cancelEditGoal(goal)"
                    class="goal-input"
                    ref="goalInput"
                  />
                  <span v-else @click="openGoal(goal.id)" @dblclick="startEditGoal(goal)" class="goal-text">
                    {{ goal.title || goal.description }}
                  </span>
                </div>
                <div class="goal-actions">
                  <span class="goal-percentage">{{ goal.progress || 0 }}%</span>
                  <button v-if="editingGoalId !== goal.id" @click="startEditGoal(goal)" class="btn-edit-goal" title="Edit goal">✏️</button>
                  <button @click="removeGoal(goal.id)" class="btn-remove-goal" title="Delete goal">×</button>
                </div>
              </div>
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: `${goal.progress || 0}%` }"
                  :class="getProgressClass(goal.progress)"
                ></div>
              </div>
            </div>
          </div>
        </div>
        <p v-else class="no-goals">No goals yet. Add one to get started.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { goalsApi } from '@/services/api'
import { useRouter } from 'vue-router'

export default {
  name: 'WorkstreamHeader',
  props: {
    workstream: {
      type: Object,
      default: null
    },
    taskCount: {
      type: Number,
      default: 0
    },
    agentCount: {
      type: Number,
      default: 0
    },
    memberCount: {
      type: Number,
      default: 0
    }
  },
  emits: ['update:description', 'update:goals', 'add-task', 'goal-created', 'goal-deleted'],
  setup(props, { emit, expose }) {
    const router = useRouter()
    const expanded = ref(true)
    const editingOverview = ref(false)
    const overviewDraft = ref('')
    const editingGoalId = ref(null)
    const overviewInput = ref(null)
    const goalInput = ref(null)

    const goals = ref([])
    const usingLegacyGoals = ref(false)

    const overallProgress = computed(() => {
      const gs = goals.value
      if (!gs || gs.length === 0) return null
      const total = gs.reduce((sum, g) => sum + (Number(g?.progress) || 0), 0)
      return Math.round(total / gs.length)
    })

    const loadGoals = async () => {
      usingLegacyGoals.value = false
      if (!props.workstream?.id) {
        goals.value = Array.isArray(props.workstream?.goals) ? [...props.workstream.goals] : []
        usingLegacyGoals.value = goals.value.length > 0
        return
      }
      try {
        const res = await goalsApi.listByWorkstream(props.workstream.id)
        const apiGoals = Array.isArray(res) ? res : (res?.goals || [])
        if (apiGoals.length > 0) {
          goals.value = apiGoals
        } else if (Array.isArray(props.workstream?.goals) && props.workstream.goals.length > 0) {
          goals.value = [...props.workstream.goals]
          usingLegacyGoals.value = true
        } else {
          goals.value = []
        }
      } catch (e) {
        console.error('Failed to load goals', e)
        goals.value = Array.isArray(props.workstream?.goals) ? [...props.workstream.goals] : []
        usingLegacyGoals.value = goals.value.length > 0
      }
    }

    watch(() => props.workstream?.id, () => { loadGoals() }, { immediate: true })

    // Expose method to parent to refresh goals (used after task completes)
    const refreshGoals = async () => {
      try { await loadGoals() } catch (e) { /* noop */ }
    }
    expose({ refreshGoals })

    const startEditOverview = () => {
      overviewDraft.value = props.workstream?.description || ''
      editingOverview.value = true
      nextTick(() => {
        overviewInput.value?.focus()
      })
    }

    const saveOverview = () => {
      if (overviewDraft.value !== props.workstream?.description) {
        emit('update:description', overviewDraft.value)
      }
      editingOverview.value = false
    }

    const cancelOverview = () => {
      overviewDraft.value = props.workstream?.description || ''
      editingOverview.value = false
    }

    const creatingGoal = ref(false)

    const addGoal = async () => {
      const payload = {
        title: 'New goal',
        description: '',
      }
      try {
        creatingGoal.value = true
        const created = props.workstream?.id
          ? await goalsApi.createForWorkstream(props.workstream.id, payload)
          : await goalsApi.create(payload)
        goals.value.push(created)
        // Emit event to parent so it can refresh its goals list
        emit('goal-created', created)
        // Don't immediately edit the goal after creation to avoid automatic save triggers
        // editingGoalId.value = created.id
        // User can double-click to edit if needed
      } catch (e) {
        console.error('Failed to create goal', e)
        alert('Failed to create goal: ' + (e.response?.data?.error || e.message))
      } finally {
        creatingGoal.value = false
      }
    }

    const originalGoalTitle = ref('')
    
    const startEditGoal = (goal) => {
      editingGoalId.value = goal.id
      originalGoalTitle.value = goal.title
      nextTick(() => {
        const inputs = goalInput.value
        if (inputs) {
          const input = Array.isArray(inputs) ? inputs[0] : inputs
          input?.focus()
          input?.select()
        }
      })
    }

    const saveGoal = async (goal) => {
      editingGoalId.value = null
      
      // Don't save if title hasn't changed
      if (goal.title === originalGoalTitle.value) {
        return
      }
      
      if (!props.workstream?.id) {
        console.error('Cannot save goal: workstream ID missing')
        alert('Cannot save goal: workstream context missing')
        return
      }
      try {
        // Format data according to backend UpdateGoalRequest struct
        const updateData = {
          title: goal.title.trim(),
          description: goal.description || '',
        }
        // Only include progress if it's defined and valid
        if (goal.progress !== undefined && goal.progress !== null) {
          updateData.progress = Number(goal.progress)
        }
        
        await goalsApi.update(props.workstream.id, goal.id, updateData)
        await loadGoals()
      } catch (e) {
        console.error('Failed to update goal', e)
        alert('Failed to update goal: ' + (e.response?.data?.error || e.message))
        // Revert the title on error
        goal.title = originalGoalTitle.value
      }
      // Backward compatible emit (no-op for parent now)
      emit('update:goals', goals.value)
    }
    
    const cancelEditGoal = (goal) => {
      editingGoalId.value = null
      goal.title = originalGoalTitle.value
    }

    const removeGoal = async (goalId) => {
      if (!props.workstream?.id) {
        console.error('Cannot delete goal: workstream ID missing')
        alert('Cannot delete goal: workstream context missing')
        return
      }
      try {
        await goalsApi.delete(props.workstream.id, goalId)
        goals.value = goals.value.filter(g => g.id !== goalId)
        // Emit event to parent so it can update its goals list
        emit('goal-deleted', goalId)
      } catch (e) {
        console.error('Failed to delete goal', e)
        alert('Failed to delete goal: ' + (e.response?.data?.error || e.message))
      }
      emit('update:goals', goals.value)
    }

    const importLegacyGoals = async () => {
      if (!props.workstream?.id) return
      const legacy = Array.isArray(props.workstream?.goals) ? props.workstream.goals : []
      if (legacy.length === 0) return
      for (const lg of legacy) {
        try {
          await goalsApi.createForWorkstream(props.workstream.id, {
            title: lg.title || lg.description || 'Goal',
            description: lg.description || ''
          })
        } catch (e) {
          console.warn('Failed to import a legacy goal', e)
        }
      }
      await loadGoals()
      usingLegacyGoals.value = false
    }


    const getProgressClass = (progress) => {
      const p = progress || 0
      if (p >= 75) return 'high'
      if (p >= 50) return 'medium'
      if (p >= 25) return 'low'
      return 'minimal'
    }

    const openGoal = (goalId) => {
      // Navigate and let GoalDetail view call the workstream-scoped endpoint
      const query = props.workstream?.id ? { ws: props.workstream.id } : {}
      router.push({ name: 'goal-detail', params: { id: goalId }, query })
    }

    return {
      expanded,
      editingOverview,
      overviewDraft,
      overviewInput,
      goals,
      usingLegacyGoals,
      creatingGoal,
      editingGoalId,
      goalInput,
      overallProgress,
      startEditOverview,
      saveOverview,
      cancelOverview,
      addGoal,
      startEditGoal,
      saveGoal,
      cancelEditGoal,
      removeGoal,
      importLegacyGoals,
      getProgressClass,
      openGoal
    }
  }
}
</script>

<style scoped>
.workstream-header {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 20px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.header-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  cursor: pointer;
  user-select: none;
}

.header-toggle:hover {
  background: rgba(0, 0, 0, 0.02);
}

.header-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  flex-wrap: wrap;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #37352f;
}

.header-stats {
  display: flex;
  gap: 12px;
  font-size: 14px;
  color: #6c757d;
}

.stat-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.toggle-btn {
  background: none;
  border: none;
  font-size: 14px;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 3px;
  transition: background 0.15s;
}

.toggle-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

/* Overall progress (header) */
.overall-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 220px;
}

.overall-progress-label {
  font-size: 12px;
  color: #6c757d;
}

.overall-progress-value {
  font-weight: 600;
  color: #37352f;
  margin-left: 4px;
}

.overall-progress-bar {
  flex: 1;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  min-width: 120px;
}

.overall-progress-fill {
  height: 100%;
  border-radius: 3px;
}

.header-content {
  padding: 0 16px 16px 16px;
  border-top: 1px solid #e9ecef;
  margin-top: -1px;
}

.workstream-header.collapsed .header-content {
  display: none;
}

.overview-section {
  margin-bottom: 16px;
}

.overview-section label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #37352f;
  margin-bottom: 6px;
}

.overview-text {
  font-size: 14px;
  color: #6c757d;
  line-height: 1.5;
  margin: 0;
  cursor: text;
  padding: 8px;
  border-radius: 4px;
  transition: background 0.15s;
}

.overview-text:hover {
  background: rgba(0, 0, 0, 0.02);
}

.edit-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.overview-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  color: #37352f;
  resize: vertical;
  transition: border-color 0.15s;
}

.overview-input:focus {
  outline: none;
  border-color: #667eea;
}

.edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.btn-save,
.btn-cancel {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-save {
  background: #667eea;
  color: white;
}

.btn-save:hover {
  background: #5568d3;
}

.btn-cancel {
  background: white;
  color: #6c757d;
  border: 1px solid #e9ecef;
}

.btn-cancel:hover {
  background: #f8f9fa;
  border-color: #cbd5e0;
}

.goals-section label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #37352f;
  margin-bottom: 6px;
}

.goals-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.goals-header .header-actions {
  display: flex;
  gap: 8px;
}

.btn-add-goal {
  padding: 4px 12px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #37352f;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-add-goal:hover {
  background: rgba(0, 0, 0, 0.02);
  border-color: #cbd5e0;
}

.goals-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.goal-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  transition: background 0.15s;
  overflow: hidden;
}

.goal-item:hover {
  background: rgba(0, 0, 0, 0.01);
}

.goal-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
}

.goal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.goal-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.goal-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.goal-bullet {
  font-size: 18px;
  color: #9ca3af;
  flex-shrink: 0;
}

.goal-text {
  font-size: 14px;
  color: #37352f;
  cursor: text;
  flex: 1;
  padding: 4px;
  border-radius: 3px;
  transition: background 0.15s;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.goal-text:hover {
  background: rgba(0, 0, 0, 0.02);
}

.goal-percentage {
  font-size: 13px;
  font-weight: 600;
  color: #6c757d;
  min-width: 40px;
  text-align: right;
}

.goal-input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #667eea;
  border-radius: 3px;
  font-size: 14px;
  font-family: inherit;
  color: #37352f;
}

.goal-input:focus {
  outline: none;
}

.btn-edit-goal,
.btn-remove-goal {
  background: none;
  border: none;
  font-size: 14px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: all 0.15s;
}

.btn-edit-goal:hover {
  background: rgba(103, 126, 234, 0.1);
  color: #667eea;
}

.btn-remove-goal {
  font-size: 18px;
}

.btn-remove-goal:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.no-goals {
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
  margin: 8px 0 0 0;
}

/* Progress Bar */
.progress-bar {
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  cursor: pointer;
  transition: height 0.2s;
}

.progress-bar:hover {
  height: 8px;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease, background-color 0.3s ease;
  position: relative;
}

.progress-fill.minimal {
  background: linear-gradient(90deg, #ef4444 0%, #f87171 100%);
}

.progress-fill.low {
  background: linear-gradient(90deg, #f97316 0%, #fb923c 100%);
}

.progress-fill.medium {
  background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
}

.progress-fill.high {
  background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
}

/* Progress Editor */
.progress-editor {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.progress-slider {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  background: #e9ecef;
}

.progress-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #667eea;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.progress-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #667eea;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.progress-slider::-webkit-slider-thumb:hover {
  background: #5568d3;
}

.progress-slider::-moz-range-thumb:hover {
  background: #5568d3;
}

.btn-done {
  padding: 4px 12px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-done:hover {
  background: #5568d3;
}
</style>
